import { archiveItems, type DateKey, nextInStack, practiceItems, savedStackDef } from '@arrow/game-engine';
import { useEffect } from 'react';

import type { PlayContext } from '../../app/play-context.ts';
import { navigate } from '../../app/router.tsx';
import type { WayOn } from '../../components/ResultSheet.tsx';
import type { Persistence } from '../../storage/persistence.ts';
import { loadArchive, loadPractice, peekArchive, peekPractice } from './archive-data.ts';
import { contextItems, contextName, itemLabel, itemName, itemPath } from './model.ts';

/**
 * The poster's way on for a game opened from Play: the next unfinished puzzle in the same
 * stack or search, after this one. The archive is fetched in the background as the game
 * opens (Play usually has it already), and read when the poster asks.
 */
export function usePlayNext(
	ctx: PlayContext | null,
	todayKey: DateKey,
	firstSeen: DateKey | null,
	persistence: Persistence,
	currentKey: string | null,
): (() => WayOn['next']) | undefined {
	useEffect(() => {
		if (!ctx || ctx.from === 'lookup') return;
		void loadArchive(todayKey).catch(() => undefined);
		void loadPractice().catch(() => undefined);
	}, [ctx, todayKey]);
	if (!ctx || !currentKey || ctx.from === 'lookup') return undefined;
	return () => {
		const archive = peekArchive(todayKey);
		if (!archive) return null;
		const local = { stats: persistence.loadStats(), progress: persistence.savedProgress() };
		const mine = persistence.myStacks().map(savedStackDef);
		const list = contextItems(ctx, archiveItems(archive.days, local, todayKey, firstSeen), practiceItems(peekPractice() ?? [], local), todayKey, currentKey, mine);
		const next = list ? nextInStack(list, currentKey) : null;
		if (!next) return null;
		const name = contextName(ctx, mine);
		return {
			lead: `Next in ${name}.`,
			label: itemName(next),
			ariaLabel: `Play the next in ${name}: ${itemLabel(next)}`,
			onGo: () => navigate(itemPath(next, { ...ctx, from: 'next' }), { replace: true }),
		};
	};
}
